import { memo, type CSSProperties } from "react";
import { useUi, useUiDispatch } from "@/app/AppContext";
import { getMatrixItemStyle } from "@/domain/color";
import { getItemLabel } from "@/domain/matrix";
import type { Intervention } from "@/domain/types";
import { useI18n } from "@/i18n/useI18n";
import type { MatrixModel } from "./useMatrixModel";

export function Matrix({ model }: { model: MatrixModel }) {
  const i18n = useI18n();
  const { showIds, showTitles, panel } = useUi();
  const dispatch = useUiDispatch();
  const { view, columns, rows, layoutId, layout } = model;
  const selectedId = panel.kind === "details" || panel.kind === "edit" ? panel.itemId : null;
  const layoutType = layout?.type ?? "ageGroups";

  return (
    <div
      id="matrix"
      className={`matrix${showTitles ? "" : " hide-item-titles"}`}
      style={{
        gridTemplateColumns: `minmax(60px, 150px) repeat(${columns.length}, minmax(60px, 1fr))`,
      }}
    >
      <div className="matrix-title">
        {i18n.language === "en" ? "Intervention matrix" : "Tiltaksmatrise"}
      </div>
      <div className="header" />
      {columns.map((column) => (
        <div key={column.key} className="header">
          <span>{i18n.columnLabel(layoutId, layoutType, column)}</span>
          {column.tooltip && (
            <span className="header-info-icon" title={column.tooltip}>
              i
            </span>
          )}
        </div>
      ))}
      {rows.map((row, index) => {
        const rowClass = `r${(index % 7) + 1}`;
        return [
          <div key={`${row.arena}:header`} className={`header ${rowClass}`}>
            {i18n.arenaLabel(row.arena)}
          </div>,
          ...columns.map((column) => (
            <div key={`${row.arena}:${column.key}`} className={`cell ${rowClass}`}>
              {row.cells[column.key]?.map((item) => {
                const value = view.values[item.codes[view.id] ?? ""];
                return (
                  <MatrixItem
                    key={item.id}
                    item={item}
                    label={getItemLabel(item, i18n.language, showIds, showTitles)}
                    title={getItemLabel(item, i18n.language, true, true)}
                    classified={!!value}
                    style={getMatrixItemStyle(value)}
                    selected={item.id === selectedId}
                    onSelect={() => dispatch({ type: "showDetails", itemId: item.id })}
                  />
                );
              })}
            </div>
          )),
        ];
      })}
    </div>
  );
}

interface MatrixItemProps {
  item: Intervention;
  label: string;
  title: string;
  classified: boolean;
  style: CSSProperties;
  selected: boolean;
  onSelect(): void;
}

const MatrixItem = memo(function MatrixItem({
  item,
  label,
  title,
  classified,
  style,
  selected,
  onSelect,
}: MatrixItemProps) {
  const classes = ["item", classified ? "" : "item-unclassified", selected ? "item-selected" : ""];
  return (
    <button
      type="button"
      className={classes.filter(Boolean).join(" ")}
      style={style}
      title={title}
      data-item-number={item.itemNumber}
      onClick={onSelect}
    >
      {label}
    </button>
  );
});
